import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn, LogOut, RefreshCw, Star, Trash2, Check } from 'lucide-react';
import { PACKAGES } from '../data';
import ScheduleManager from '../components/ScheduleManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'emd_admin_token';

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState('bookings');
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      toast.success('Logged in.');
    } catch (err) {
      toast.error('Invalid password.');
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setBookings([]);
    setContacts([]);
    setReviews([]);
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [b, c, r] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: { 'x-admin-token': token } }),
        axios.get(`${API}/admin/contacts`, { headers: { 'x-admin-token': token } }),
        axios.get(`${API}/admin/reviews`, { headers: { 'x-admin-token': token } }),
      ]);
      setBookings(b.data);
      setContacts(c.data);
      setReviews(r.data);
    } catch (err) {
      if (err.response?.status === 401) logout();
      toast.error('Could not load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${id}?status=${status}`, null, { headers: { 'x-admin-token': token } });
      toast.success('Updated.');
      load();
    } catch (err) {
      toast.error('Update failed.');
    }
  };

  const toggleReviewApproval = async (id, approved) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}?approved=${approved}`, null, { headers: { 'x-admin-token': token } });
      toast.success(approved ? 'Review approved.' : 'Review unapproved.');
      load();
    } catch (err) {
      toast.error('Update failed.');
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: { 'x-admin-token': token } });
      toast.success('Review deleted.');
      load();
    } catch (err) {
      toast.error('Delete failed.');
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="bg-[#141414] border border-white/5 rounded-2xl p-8 w-full max-w-md">
          <h1 className="font-display text-3xl mb-6">Admin Login</h1>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 mb-4"
            required
          />
          <button data-testid="admin-login-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2">
            <LogIn className="w-4 h-4" /> Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0a0a0a] text-white pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Admin Dashboard</h1>
            <p className="text-zinc-400 text-sm mt-1">{bookings.length} bookings · {contacts.length} messages</p>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh" className="px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 text-sm inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
            <button onClick={logout} data-testid="admin-logout" className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm inline-flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button onClick={() => setTab('bookings')} data-testid="tab-bookings" className={`px-5 py-2 rounded-full text-sm ${tab === 'bookings' ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>Bookings ({bookings.length})</button>
          <button onClick={() => setTab('schedule')} data-testid="tab-schedule" className={`px-5 py-2 rounded-full text-sm ${tab === 'schedule' ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>Schedule</button>
          <button onClick={() => setTab('reviews')} data-testid="tab-reviews" className={`px-5 py-2 rounded-full text-sm ${tab === 'reviews' ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>Reviews ({reviews.length})</button>
          <button onClick={() => setTab('contacts')} data-testid="tab-contacts" className={`px-5 py-2 rounded-full text-sm ${tab === 'contacts' ? 'bg-blue-600 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}>Messages ({contacts.length})</button>
        </div>

        {tab === 'schedule' && <ScheduleManager token={token} />}

        {tab === 'reviews' && (
          <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
            {reviews.length === 0 ? (
              <p className="px-5 py-10 text-center text-zinc-500">No reviews yet.</p>
            ) : (
              <div className="divide-y divide-white/5">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-row-${r.id}`} className="p-5 md:p-6 flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium">{r.name}</span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-blue-400 text-blue-400" />)}
                        </div>
                        {r.approved
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-300 border border-green-500/30">Approved</span>
                          : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">Pending</span>}
                      </div>
                      {r.vehicle && <div className="text-xs text-zinc-500 mb-2">{r.vehicle}</div>}
                      <p className="text-zinc-300 text-sm leading-relaxed">{r.message}</p>
                      {r.email && <div className="text-xs text-zinc-500 mt-2">{r.email}</div>}
                    </div>
                    <div className="flex md:flex-col gap-2 md:items-end">
                      <button
                        onClick={() => toggleReviewApproval(r.id, !r.approved)}
                        data-testid={`review-toggle-${r.id}`}
                        className={`px-4 py-2 rounded-full text-xs inline-flex items-center gap-1.5 ${r.approved ? 'bg-white/10 hover:bg-white/20' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                      >
                        <Check className="w-3.5 h-3.5" /> {r.approved ? 'Unapprove' : 'Approve'}
                      </button>
                      <button
                        onClick={() => deleteReview(r.id)}
                        data-testid={`review-delete-${r.id}`}
                        className="px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs inline-flex items-center gap-1.5 border border-red-500/30"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'bookings' && (
          <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-white/5">
                  <tr>
                    <th className="px-5 py-3">Customer</th>
                    <th className="px-5 py-3">Vehicle</th>
                    <th className="px-5 py-3">Package</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Total</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr><td colSpan="6" className="px-5 py-10 text-center text-zinc-500">No bookings yet.</td></tr>
                  )}
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-5 py-4">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-zinc-500">{b.email}</div>
                        <div className="text-xs text-zinc-500">{b.phone}</div>
                        <div className="text-xs text-zinc-500 mt-1">{b.address}</div>
                      </td>
                      <td className="px-5 py-4 capitalize">{b.vehicle_type}</td>
                      <td className="px-5 py-4">
                        <div>{PACKAGES[b.package]?.name}</div>
                        {b.addons?.length > 0 && <div className="text-xs text-zinc-500">+{b.addons.length} add-ons</div>}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">{b.preferred_date}<br/><span className="text-xs text-zinc-500">{b.preferred_time}</span></td>
                      <td className="px-5 py-4 text-blue-400 font-medium">${b.estimated_total}</td>
                      <td className="px-5 py-4">
                        <select
                          value={b.status}
                          onChange={(e) => updateStatus(b.id, e.target.value)}
                          data-testid={`status-select-${b.id}`}
                          className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-blue-500"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'contacts' && (
          <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="contacts-table">
                <thead className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-white/5">
                  <tr>
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Contact</th>
                    <th className="px-5 py-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && (
                    <tr><td colSpan="3" className="px-5 py-10 text-center text-zinc-500">No messages yet.</td></tr>
                  )}
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b border-white/5">
                      <td className="px-5 py-4 font-medium align-top">{c.name}</td>
                      <td className="px-5 py-4 align-top text-xs">
                        <div>{c.email}</div>
                        {c.phone && <div className="text-zinc-500">{c.phone}</div>}
                      </td>
                      <td className="px-5 py-4 text-zinc-300">{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
